import React from 'react';

import { NextPageWithLayout } from 'next';

import { AppDownloadSection } from '../../../components/AppDownloadSection/AppDownloadSection';
import { Container } from '../../../components/Container/Container';
import { MetaTags } from '../../../components/MetaTags/MetaTags';
import { CircleImageAndText } from '../components/CircleImageAndText';

import phoneFour from './assets/phoneFour.png';
import phoneOne from './assets/phoneOne.png';
import phoneThree from './assets/phoneThree.png';
import phoneTwo from './assets/phoneTwo.png';
import { HeaderSection } from '../components/HeaderSection';
import {
  META_TOP_WATERS_TITLE,
  META_TOP_WATERS_DESCRIPTION,
  META_TOP_WATERS_CANONICAL,
} from '../../../constants/metadata';
import { Heading } from '../../../components/Heading/Heading';
import { Text } from '../../../components/Text/Text';
import { BlueWaterIcon } from '../../../components/Icons/BlueOnBlue/BlueWaterIcon';
import { BlueFishIcon } from '../../../components/Icons/BlueOnBlue/BlueFishIcon';

export const TopWatersPage: NextPageWithLayout = () => {
  const headerDescription = `Sometimes there are just too many fishy waters to sort through and sometimes you're looking for something really specific. 
  Top waters helps you narrow down all the fishing waters near you by organizing them in a tab and showing you what the potential is for big fish in each. 
  You can also get weekly updated reports about fishing activity and how tricky, or amazing, the fishing has been.`;

  return (
    <Container className="p-0 sm:p-0 md:p-0">
      <MetaTags
        title={META_TOP_WATERS_TITLE}
        description={META_TOP_WATERS_DESCRIPTION}
        link={META_TOP_WATERS_CANONICAL}
      />

      <HeaderSection
        headingText="Pinpoint high-quality water with reports, trophy potential and species filters."
        bodyText={headerDescription}
      />

      <CircleImageAndText
        imgSrc={phoneOne}
        imgAlt="All your fishing waters in one place in the Fishbrain app"
        headingText="All your fishing waters in one place"
        bodyText="You asked and we listened. The Waters tab gives you a list of all your fishing waters streamlined and sorted by most catches to least.
Scroll through nearby waters, view how many catches have been made, find their location and start fishing."
      />

      <CircleImageAndText
        imgSrc={phoneTwo}
        imgRight
        imgAlt="Find fishing spots with species filters and fishing reports in the Fishbrain app"
        headingText="Find fishing spots with species filters and fishing reports"
        bodyText="Whether you’re looking for a specific species to fish for, or the water with the most action, we’ve got you covered. You can filter your Waters tab by species to show waters with the most catches of that species. You can also check the report gauge to see how hot the fishing is, based on the week’s catches."
      />

      <CircleImageAndText imgSrc={phoneThree} imgAlt="Fishing regulation made easy">
        <div className="sm:w-col-7 md:w-col-5 mt-6">
          <div className="flex gap-3">
            <BlueFishIcon size={32} />
            <Heading variant="sm">Find the species you’re looking for</Heading>
          </div>
          <Text variant="lg" className="mt-4">
            Filter the waters you see for the fish you want to catch. Only waters with that species
            will show up, listed from most catches to least.
          </Text>
        </div>

        <div className="sm:w-col-7 md:w-col-5 mt-6">
          <div className="flex gap-3">
            <BlueWaterIcon size={32} />
            <Heading variant="sm">Which waters have the best fishing right now?</Heading>
          </div>
          <Text variant="lg" className="mt-4">
            The fishing quality gauge will show you if the fishing has been tricky, or great by
            using the amount of catches made on each water during the week
          </Text>
        </div>
      </CircleImageAndText>

      <CircleImageAndText
        imgSrc={phoneFour}
        imgRight
        imgAlt="Tropy potential in the Fishbrain app"
        headingText="Look no further for trophy potential"
        bodyText={
          'Sometimes you want a fish that doesn’t need a fish story, and Big Fish Potential can help. It’s a gauge that shows the potential of catching an above-average fish in different waters. Tap on the gauge to see a graph charting the average fish size in each water, and how big "above average" really is. Big Fish Potential also shows the state record for each water.'
        }
      />

      <AppDownloadSection />
    </Container>
  );
};
